import React, { useState, useCallback } from 'react';
import style from './style.module.scss';
import { Checkbox } from '@ontruck/orc';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { makeRoutePairs } from 'reselects/routes';

const RoutesTree = ({ routes, allRoutes, intl, onChange }) => {
  const t = intl.messages.components.CarrierDetails;
  const internationalCount = allRoutes.filter((item) => item.isInternational).length;
  const nationalCount = allRoutes.filter((item) => !item.isInternational).length;
  const pairedRoutes = makeRoutePairs(routes);

  const [submitting, setSubmitting] = useState(false);

  const sendCheckedRoutes = useCallback(
    async (newRoutes) => {
      setSubmitting(true);
      await onChange(newRoutes);
      setSubmitting(false);
    },
    [setSubmitting, onChange]
  );

  const mapReversedRoutes = useCallback((routes) => {
    return routes.reduce((acc, item) => {
      if (item.origin === item.destination)
        return acc.concat({ origin: item.origin, destination: item.destination });
      return acc.concat(
        { origin: item.origin, destination: item.destination },
        { origin: item.destination, destination: item.origin }
      );
    }, []);
  }, []);

  const handleChange = useCallback(
    (name, value) => {
      if (value) {
        const route = allRoutes.find((item) => item.symmetric_name.includes(name));
        sendCheckedRoutes([...routes, ...mapReversedRoutes([route])]);
      } else {
        sendCheckedRoutes(routes.filter((item) => !item.symmetric_name.includes(name)));
      }
    },
    [sendCheckedRoutes, allRoutes, routes, mapReversedRoutes]
  );

  const toggleGroupRoutes = useCallback(
    (isInternational, isAdding) => {
      if (isAdding) {
        const routesGroup = isInternational
          ? allRoutes.filter((item) => item.isInternational)
          : allRoutes.filter((item) => !item.isInternational);
        sendCheckedRoutes([...routes, ...mapReversedRoutes(routesGroup)]);
      } else {
        sendCheckedRoutes(routes.filter((route) => route.isInternational !== isInternational));
      }
    },
    [sendCheckedRoutes, allRoutes, routes, mapReversedRoutes]
  );

  const renderChecks = (routesArr) => {
    return routesArr.map((route) => {
      return (
        <div key={route.symmetric_name} className={style.routeCheckbox} data-testid="checkbox">
          <Checkbox
            name={route.name}
            label={route.simple_name}
            size="M"
            girded
            disabled={submitting}
            checked={
              pairedRoutes.findIndex((item) =>
                route.symmetric_name.includes(item.symmetric_name)
              ) !== -1
            }
            onChange={(e, { name, value }) => handleChange(name, value)}
          />
        </div>
      );
    });
  };

  return (
    <div className={style.routesTree} data-testid="routesTree">
      <h2 className={style.sectionTitle}>{t.routes_title}</h2>
      <div className={style.routesTreeContainer}>
        {internationalCount > 0 && (
          <Checkbox
            name="international"
            label={t.international}
            size="L"
            disabled={submitting}
            checked={
              pairedRoutes.filter((item) => item.isInternational).length === internationalCount
            }
            onChange={(e, { value }) => toggleGroupRoutes(true, value)}
          />
        )}
        <div className={style.routesList}>
          {renderChecks(allRoutes.filter((route) => route.isInternational))}
        </div>
      </div>
      <div className={style.routesTreeContainer}>
        {nationalCount > 0 && (
          <Checkbox
            name="national"
            label={t.national}
            size="L"
            disabled={submitting}
            checked={pairedRoutes.filter((item) => !item.isInternational).length === nationalCount}
            onChange={(e, { value }) => toggleGroupRoutes(false, value)}
          />
        )}
        <div className={style.routesList}>
          {renderChecks(allRoutes.filter((route) => !route.isInternational))}
        </div>
      </div>
    </div>
  );
};

RoutesTree.propTypes = {
  routes: PropTypes.array.isRequired,
  allRoutes: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default injectIntl(RoutesTree);
